/**
 * `first_message_preview` renderer — composes `@nextday/funnel-ui`
 * `FirstMessagePreview` (the typing-dots-then-text bubble shipped in
 * NX-9298). The terminal step in the SecretMate funnel: the user sees
 * their character "typing" their first message, then taps the CTA which
 * fires `onAdvance()` so the orchestrator can route to the actual chat.
 *
 * Character metadata (name + avatar) is sourced in priority order:
 * 1. Explicit `data.characterName` / `data.characterAvatarUrl`.
 * 2. `session.matched_character` (single-character SecretMate flow).
 * 3. `session.character` (legacy character continuity).
 *
 * `data.message` may contain `{character_name}` and `{display_name}`
 * tokens which the renderer interpolates from session/answers at render
 * time. This keeps message copy fully JSON-authorable while still
 * personalizing without a backend round-trip.
 *
 * Step JSON shape (plan §5 step 14 — terminal):
 *
 *   {
 *     "type": "first_message_preview",
 *     "data": {
 *       "message": "Hey {display_name}, I've been waiting for you. ...",
 *       "ctaLabel": "Send your first message",
 *       "displayNameAnswerKey": "display_name",
 *       "typingMs": 700
 *     }
 *   }
 */
import { FirstMessagePreview } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

export const firstMessagePreviewSchema = z
  .object({
    message: z.string().min(1),
    ctaLabel: z.string().min(1),
    /** Override character name; defaults to session character name. */
    characterName: z.string().min(1).optional(),
    /** Override character avatar; defaults to session character image. */
    characterAvatarUrl: z.string().optional(),
    /** Answer key carrying the user's display name for `{display_name}`. */
    displayNameAnswerKey: z.string().min(1).optional(),
    typingMs: z.number().int().nonnegative().optional(),
    disabledInputPlaceholder: z.string().optional(),
  })
  .strict();

export type FirstMessagePreviewData = z.infer<typeof firstMessagePreviewSchema>;

const pickString = (candidates: ReadonlyArray<unknown>, fallback: string): string => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }
  return fallback;
};

const interpolate = (template: string, characterName: string, displayName: string): string =>
  template.replaceAll("{character_name}", characterName).replaceAll("{display_name}", displayName);

const FirstMessagePreviewRenderer = ({
  data,
  step,
  answers,
  session,
  onAdvance,
}: RendererProps<FirstMessagePreviewData>) => {
  const matched = session.matched_character ?? {};
  const fallback = session.character ?? { id: "", name: "" };

  const characterName = pickString(
    [data.characterName, (matched as { name?: unknown }).name, fallback.name],
    "Your match",
  );

  // Mirror the priority used by `character_reveal_gate`'s
  // `normalizeCharacter` (imageUrl → primary_image_url → safe_image_url)
  // so a `matched_character` whose avatar lives under the canonical
  // `imageUrl` key (the shape used by `characterShapeSchema`) renders
  // on both screens. Previously we only checked the two snake_case
  // backend keys and the avatar would silently disappear here.
  const characterAvatarUrl = pickString(
    [
      data.characterAvatarUrl,
      (matched as { imageUrl?: unknown }).imageUrl,
      (matched as { primary_image_url?: unknown }).primary_image_url,
      (matched as { safe_image_url?: unknown }).safe_image_url,
      fallback.imageUrl,
      fallback.primary_image_url,
      fallback.safe_image_url,
    ],
    "",
  );

  const displayName = data.displayNameAnswerKey
    ? pickString([answers[data.displayNameAnswerKey]], "you")
    : "you";

  return (
    <FirstMessagePreview
      characterName={characterName}
      characterAvatarUrl={characterAvatarUrl || undefined}
      message={interpolate(data.message, characterName, displayName)}
      ctaLabel={data.ctaLabel}
      typingMs={data.typingMs}
      disabledInputPlaceholder={data.disabledInputPlaceholder}
      onCta={() => onAdvance()}
      testId={`renderer-first-message-${step.id}`}
    />
  );
};

export const firstMessagePreviewRenderer = createRenderer({
  type: "first_message_preview",
  dataSchema: firstMessagePreviewSchema,
  label: "First message preview",
  Component: FirstMessagePreviewRenderer,
});

export default firstMessagePreviewRenderer;
